import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import mcpServiceService from '../../services/mcpServiceService';
import Spinner from '../shared/Spinner';
import { apiErrorMessage } from '../../utils/apiError';
import '../shared/MqQueueModal.css';
import '../OxiDequeueAdmin.css';   // .acp-table — the house list table
import './McpBackendTab.css';

// THE HCONNECT MCP BACKEND view, under the Content tab of /mcp-service/hotels/:hotelId — everything the hotel's pipeline has delivered to the MCP
// Backend, read back through the hotel's MCP Backend binding (GET /pipelines/mcp-service/hotels/{hotelId}/backend,
// integrations/mcpviewer.js):
//
//   Availability cache  freshness, room types, rate plans
//   Bookings            the last 30 days per AI client — counts and revenue, never a guest
//   Content             published items, the fact sheet, the questions it could not answer
//
// A read the binding's credential is not granted shows its refusal in its own place; the rest still show. Read-only.

// The one list in an answer ({ roomTypes: [...] }, { items: [...] }, { clients: [...] }), when there is one.
const listOf = (body) => {
    if (Array.isArray(body)) return body;
    if (!body || typeof body !== 'object') return null;
    const arrays = Object.values(body).filter(Array.isArray);
    return arrays.length === 1 ? arrays[0] : null;
};

// A cell: a primitive as text, anything else as compact JSON.
const cell = (v) => (v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));

// A list as a table — the columns its rows have most in common, at most eight, so a wide document stays readable;
// the whole row is a click away.
const ListTable = ({ rows }) => {
    const [open, setOpen] = useState(null);
    if (!rows.length) return <p className="mqm-empty">Nothing here yet.</p>;
    const counts = new Map();
    for (const r of rows.slice(0, 50)) for (const k of Object.keys(r || {})) counts.set(k, (counts.get(k) || 0) + 1);
    const cols = [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8).map(([k]) => k);
    return (
        <>
            <table className="acp-table mqm-table mbv-table">
                <thead><tr>{cols.map(c => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className={`mbv-row${open === i ? ' is-open' : ''}`} onClick={() => setOpen(open === i ? null : i)}>
                            {cols.map(c => <td key={c} title={cell(r && r[c])}>{cell(r && r[c])}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
            {open != null && <pre className="mbv-json">{JSON.stringify(rows[open], null, 2)}</pre>}
            <p className="acm-hint mqm-note">{rows.length} row(s). Click one for all of it.</p>
        </>
    );
};

const Read = ({ read }) => {
    const list = read.body !== undefined ? listOf(read.body) : null;
    return (
        <section className="mbv-read">
            <h4 className="mbv-read-title">
                {read.label}
                {read.status && <span className="mqm-muted"> · HTTP {read.status}</span>}
            </h4>
            {read.error
                ? <div className="os-msg os-msg-error">{read.error}</div>
                : list
                    ? <ListTable rows={list} />
                    : <pre className="mbv-json">{JSON.stringify(read.body, null, 2)}</pre>}
        </section>
    );
};

export default function McpBackendTab({ hotel }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState('ari');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await mcpServiceService.getBackend(hotel.hotelId));
        } catch (e) {
            setError(apiErrorMessage(e, 'The MCP Backend could not be read'));
        } finally {
            setLoading(false);
        }
    }, [hotel.hotelId]);
    useEffect(() => { load(); }, [load]);

    if (error && !data) return <div className="os-msg os-msg-error">{error}</div>;
    if (!data) return <div className="loading-section"><Spinner /></div>;
    if (data.missing) {
        return (
            <div className="mcps-card">
                <h3>Nothing to read yet</h3>
                <p className="mcps-meta">{data.missing}</p>
            </div>
        );
    }
    const section = data.sections.find(s => s.key === tab) || data.sections[0];

    return (
        <div className="mcps-section">
            <p className="mcps-meta">
                Everything {hotel.hotelName}&rsquo;s pipeline has delivered to the {data.system || 'MCP Backend'}, read back
                through its binding <code>{data.xHotelId}</code>. Bookings are counts only — no guest data. Read-only.
            </p>
            <div className="mqm-tally">
                <nav className="mbv-tabs" aria-label="Section">
                    {data.sections.map(s => (
                        <button key={s.key} type="button" className={`mbv-tab${section && section.key === s.key ? ' is-on' : ''}`}
                            onClick={() => setTab(s.key)}>
                            {s.title}
                        </button>
                    ))}
                </nav>
                <span className="mqm-grow" />
                <button type="button" className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
                    <RefreshCw size={14} /> {loading ? 'Refreshing…' : 'Refresh'}
                </button>
            </div>
            {error && <div className="os-msg os-msg-error">{error}</div>}
            {section && section.reads.map(r => <Read key={r.label} read={r} />)}
        </div>
    );
}
